/**
 * Forberedelsesfaktor (DK + deactivatePools): vægtet UV × faktor med override per fag/opgave.
 * Norsk årsramme i `preparation` bruges ikke her — kun når locale/schoolCountry er DK.
 */

export function parseNumericPreparationFactor (preparation) {
  if (preparation === null || preparation === undefined || preparation === '') {
    return null
  }
  const n = typeof preparation === 'number' ? preparation : parseFloat(String(preparation).replace(',', '.'))
  return isNaN(n) ? null : n
}

export function sameCalendarId (a, b) {
  if (a === null || a === undefined || a === '' || b === null || b === undefined || b === '') {
    return false
  }
  const na = Number(a)
  const nb = Number(b)
  return !isNaN(na) && na === nb
}

/**
 * DK forberedelsesfaktor (override på fag/opgave): skal ligge i [-1, 1].
 * Negativ faktor bruges fx til 6. ferieuge (negative undervisningstimer × negativ
 * faktor modregner timerne i forberedelse).
 * null/undefined/tom streng → null (brug skolens standard).
 */
export function clampDanishPreparationFactor (preparation) {
  const parsed = parseNumericPreparationFactor(preparation)
  if (parsed === null) {
    return null
  }
  if (parsed < -1) {
    return -1
  }
  if (parsed > 1) {
    return 1
  }
  return parsed
}

/**
 * Per-enhed faktor: eksplicit værdi på fag/opgave overstyrer skolens prepFactor.
 */
export function effectivePreparationFactor (entityPreparation, schoolPrepFactorNumeric) {
  const parsedEntity = parseNumericPreparationFactor(entityPreparation)
  if (parsedEntity !== null) {
    return parsedEntity
  }
  return schoolPrepFactorNumeric
}

export function getVHoursForSubject (subject, userCalId) {
  if (!subject) return 0

  if (Array.isArray(subject.subject_calendars)) {
    const userMatch = subject.subject_calendars.find(sc =>
      sameCalendarId(sc.calendar_id, userCalId) && sc.hours != null
    )
    if (userMatch) {
      const parsed = parseFloat(userMatch.hours)
      return isNaN(parsed) ? 0 : parsed
    }
  }

  if (Array.isArray(subject.cals)) {
    const klass = subject.cals.find(c => c.calendar_type === 'klass')
    if (klass && Array.isArray(subject.subject_calendars)) {
      const klassMatch = subject.subject_calendars.find(sc =>
        sc.calendar_id === klass.id && sc.hours != null
      )
      if (klassMatch) {
        const parsed = parseFloat(klassMatch.hours)
        return isNaN(parsed) ? 0 : parsed
      }
    }
  }

  const fallback = parseFloat(subject.hours)
  return isNaN(fallback) ? 0 : fallback
}

function getTeachingHoursForTask (task, calId, allUsersInCalIdHash) {
  const userId = allUsersInCalIdHash[calId] ? allUsersInCalIdHash[calId].id : null
  if (!userId) {
    return task.hours * 1
  }
  let idx = task.teachers && task.teachers[0] ? task.teachers.findIndex(teacher => teacher.user_id === userId) : -1
  if (idx > -1) {
    return task.teachers[idx].hours * 1
  }
  return task.hours * 1
}

/**
 * Sum af timer fra forberedelsesopgaver og -fag med eksplicit preparation-faktor (ikke null).
 * Bruges selvstændigt for grupper uden forberedelsesfaktor, og som del af den vægtede beregning.
 */
export function sumExplicitPrepTaskHoursForCalendar (calId, state, rootGetters) {
  const allUsersInCalIdHash = rootGetters.allUsersInCalIdHash || {}
  let total = 0
  state.tasks.forEach(task => {
    if (task.subject_type === 'preparation' && Array.isArray(task.user_calendar_ids)) {
      const factor = parseNumericPreparationFactor(task.preparation)
      if (factor === null) {
        return
      }
      task.user_calendar_ids.forEach(ucalId => {
        if (sameCalendarId(ucalId, calId)) {
          const hours = getTeachingHoursForTask(task, calId, allUsersInCalIdHash)
          total += hours * factor
        }
      })
    }
  })
  state.subjects.forEach(subject => {
    if (subject.subject_type === 'preparation' && subject.task_overview !== false && Array.isArray(subject.cals)) {
      const factor = parseNumericPreparationFactor(subject.preparation)
      if (factor === null) {
        return
      }
      subject.cals.forEach(calendar => {
        if (calendar.calendar_type === 'user' && sameCalendarId(calendar.id, calId)) {
          const hours = getVHoursForSubject(subject, calId)
          total += hours * factor
        }
      })
    }
  })
  return parseFloat(total.toFixed(2))
}

/**
 * Sum af UV-timer × effektiv faktor for undervisningsfag og -opgaver på kalenderen, plus
 * forberedelsesopgaver og -fag (subject_type preparation) med eksplicit faktor. subject_type
 * preparation tælles ikke i undervisningsopgave-løkken (undgår dobbelttælling).
 *
 * @returns {number|null} — forberedelsestimer for bruger-kalender, eller null hvis vægtet model ikke gælder
 */
export function computeWeightedPreparationHoursForCalendar (calId, state, rootGetters, rootState) {
  const schoolSettings = rootState.schoolSettings || {}
  const deactivatePools = schoolSettings.deactivatePools
  const schoolPrepRaw = schoolSettings.prepFactor
  const schoolPrepFactorNumeric = parseNumericPreparationFactor(schoolPrepRaw)

  const locale = rootGetters.locale
  const schoolCountry = rootGetters.schoolCountry

  if (!deactivatePools || locale !== 'da' || String(schoolCountry).toLowerCase() !== 'dk') {
    return null
  }
  if (schoolPrepFactorNumeric === null) {
    return null
  }

  const allUsersInCalIdHash = rootGetters.allUsersInCalIdHash || {}

  let total = 0
  let hasMatchingItems = false

  state.subjects.forEach(subject => {
    if (subject.master_type === 'teaching' && subject.task_overview !== false && Array.isArray(subject.cals)) {
      subject.cals.forEach(calendar => {
        if (calendar.calendar_type === 'user' && sameCalendarId(calendar.id, calId)) {
          hasMatchingItems = true
          const hours = getVHoursForSubject(subject, calId)
          const factor = effectivePreparationFactor(subject.preparation, schoolPrepFactorNumeric)
          total += hours * factor
        }
      })
    }
  })

  state.tasks.forEach(task => {
    if (task.subject_type === 'preparation') {
      return
    }
    if (task.master_type === 'teaching' && Array.isArray(task.cals)) {
      task.cals.forEach(calendar => {
        if (calendar.calendar_type === 'user' && sameCalendarId(calendar.id, calId)) {
          hasMatchingItems = true
          const hours = getTeachingHoursForTask(task, calId, allUsersInCalIdHash)
          const factor = effectivePreparationFactor(task.preparation, schoolPrepFactorNumeric)
          total += hours * factor
        }
      })
    }
  })

  total += sumExplicitPrepTaskHoursForCalendar(calId, state, rootGetters)

  if (!hasMatchingItems && total === 0) {
    return null
  }

  return parseFloat(total.toFixed(2))
}

function calendarKey (calId) {
  if (calId === null || calId === undefined || calId === '') {
    return null
  }
  const n = Number(calId)
  return isNaN(n) ? null : n
}

function addToKey (obj, key, value) {
  obj[key] = (obj[key] || 0) + value
}

/**
 * Samme resultat som sumExplicitPrepTaskHoursForCalendar / computeWeightedPreparationHoursForCalendar,
 * men for alle kalendere i ét gennemløb af fag og opgaver.
 *
 * @returns {{explicit: Object, weighted: Object|null}} — kalender-id -> timer. `weighted` er null hvis
 * vægtet model ikke gælder; kalendere uden match mangler i `weighted` (svarer til null).
 */
export function computePreparationHoursByCalendar (state, rootGetters, rootState) {
  const allUsersInCalIdHash = rootGetters.allUsersInCalIdHash || {}
  const explicitRaw = {}

  state.tasks.forEach(task => {
    if (task.subject_type === 'preparation' && Array.isArray(task.user_calendar_ids)) {
      const factor = parseNumericPreparationFactor(task.preparation)
      if (factor === null) {
        return
      }
      task.user_calendar_ids.forEach(ucalId => {
        const key = calendarKey(ucalId)
        if (key !== null) {
          addToKey(explicitRaw, key, getTeachingHoursForTask(task, ucalId, allUsersInCalIdHash) * factor)
        }
      })
    }
  })
  state.subjects.forEach(subject => {
    if (subject.subject_type === 'preparation' && subject.task_overview !== false && Array.isArray(subject.cals)) {
      const factor = parseNumericPreparationFactor(subject.preparation)
      if (factor === null) {
        return
      }
      subject.cals.forEach(calendar => {
        const key = calendarKey(calendar.id)
        if (calendar.calendar_type === 'user' && key !== null) {
          addToKey(explicitRaw, key, getVHoursForSubject(subject, calendar.id) * factor)
        }
      })
    }
  })

  const explicit = {}
  Object.keys(explicitRaw).forEach(key => {
    explicit[key] = parseFloat(explicitRaw[key].toFixed(2))
  })

  const schoolSettings = rootState.schoolSettings || {}
  const schoolPrepFactorNumeric = parseNumericPreparationFactor(schoolSettings.prepFactor)
  if (!schoolSettings.deactivatePools || rootGetters.locale !== 'da' ||
    String(rootGetters.schoolCountry).toLowerCase() !== 'dk' || schoolPrepFactorNumeric === null) {
    return {explicit, weighted: null}
  }

  const teachingRaw = {}
  state.subjects.forEach(subject => {
    if (subject.master_type === 'teaching' && subject.task_overview !== false && Array.isArray(subject.cals)) {
      subject.cals.forEach(calendar => {
        const key = calendarKey(calendar.id)
        if (calendar.calendar_type === 'user' && key !== null) {
          const factor = effectivePreparationFactor(subject.preparation, schoolPrepFactorNumeric)
          addToKey(teachingRaw, key, getVHoursForSubject(subject, calendar.id) * factor)
        }
      })
    }
  })
  state.tasks.forEach(task => {
    if (task.subject_type === 'preparation') {
      return
    }
    if (task.master_type === 'teaching' && Array.isArray(task.cals)) {
      task.cals.forEach(calendar => {
        const key = calendarKey(calendar.id)
        if (calendar.calendar_type === 'user' && key !== null) {
          const factor = effectivePreparationFactor(task.preparation, schoolPrepFactorNumeric)
          addToKey(teachingRaw, key, getTeachingHoursForTask(task, calendar.id, allUsersInCalIdHash) * factor)
        }
      })
    }
  })

  const weighted = {}
  Object.keys(teachingRaw).forEach(key => {
    weighted[key] = parseFloat((teachingRaw[key] + (explicit[key] || 0)).toFixed(2))
  })
  Object.keys(explicit).forEach(key => {
    if (!(key in weighted) && explicit[key] !== 0) {
      weighted[key] = parseFloat(explicit[key].toFixed(2))
    }
  })
  return {explicit, weighted}
}

/**
 * Vægtet forberedelse fra en bruger-opgaveliste (api.users.tasks).
 * Bruges på opgaveoversigten, hvor state.subjects/tasks ikke nødvendigvis er loadet.
 */
export function computeWeightedPreparationFromTaskList (tasks, schoolPrepFactorNumeric, options) {
  const skipTeachingFactor = options && options.skipTeachingFactor
  if (!Array.isArray(tasks)) {
    return 0
  }
  let total = 0
  tasks.forEach(task => {
    if (!task || task.substitute) {
      return
    }
    const isPrepType = task.subject_type === 'preparation' || task.master_type === 'preparation'
    if (isPrepType) {
      const factor = parseNumericPreparationFactor(task.preparation)
      if (factor !== null) {
        const hours = task.hours ? parseFloat(task.hours) : 0
        total += hours * factor
      }
      return
    }
    if (!skipTeachingFactor && task.master_type === 'teaching') {
      const hours = task.hours ? parseFloat(task.hours) : 0
      const factor = effectivePreparationFactor(task.preparation, schoolPrepFactorNumeric)
      if (factor !== null) {
        total += hours * factor
      }
    }
  })
  return parseFloat(total.toFixed(2))
}



// WEBPACK FOOTER //
// ./src/utils/preparationFactor.js